'use strict';

var _require = require('../constants/mediaShuttle/portalAuthConfigTypes'),
    MEDIA_SHUTTLE = _require.MEDIA_SHUTTLE,
    PLATFORM_SAML = _require.PLATFORM_SAML;

var _require2 = require('lodash'),
    isNil = _require2.isNil;

var nonSaml2Types = new Set([MEDIA_SHUTTLE, PLATFORM_SAML]);
var isSaml2 = function isSaml2(type) {
    return !nonSaml2Types.has(type);
};
var isPlatformSaml = function isPlatformSaml(_ref) {
    var type = _ref.type;
    return type === PLATFORM_SAML;
};
var isMediaShuttle = function isMediaShuttle(_ref2) {
    var type = _ref2.type;
    return type === MEDIA_SHUTTLE;
};

/**
 * Checks whether the portal with the specified authConfiguration is accessible by a user
 * with the specified loginIdentifier and userEmailDomain. This deduction falls under three cases (considered top down):
 * 1. User is SAML 2.0 domain restricted -> Not accessible
 * 2. User is Platform domain restricted -> Accessible if and only if the loginIdentifiers match.
 * 3. User has MediaShuttle -> Accessible.
 * Precondition: It is assumed that the portals with the supplied auth configs requires authentication.
 * Hence, a portal with empty auth configs is deemed inaccessible.
 */
var isPortalAccessible = function isPortalAccessible(_ref3) {
    var _ref3$authConfigurati = _ref3.authConfigurations,
        authConfigurations = _ref3$authConfigurati === undefined ? [] : _ref3$authConfigurati,
        loginIdentifier = _ref3.loginIdentifier,
        userEmailDomain = _ref3.userEmailDomain;

    /**
     * We may not always *need* the userEmailDomain but we should always have it. If the value is empty
     * return false as a precaution.
     */
    if (isNil(userEmailDomain)) {
        return false;
    }
    /**
     * We return false for any restricted domain match with a SAML 2.0 portal since there is
     * no way to verify the identity provider.
     */
    var isSaml2DomainRestricted = authConfigurations.some(function (_ref4) {
        var type = _ref4.type,
            restrictedDomains = _ref4.restrictedDomains;
        return isSaml2(type) && !isNil(restrictedDomains) && restrictedDomains.includes(userEmailDomain);
    });
    if (isSaml2DomainRestricted) {
        return false;
    }
    /**
     * At this point, we may still have SAML 2.0 portals in the authConfigurations but they
     * can be safely ignored since we don't match with any of their restricted domains.
     * The exclusively Saml 2.0 case is handled below.
     */

    var platformSamlConfiguration = authConfigurations.find(isPlatformSaml);
    var mediaShuttleConfiguration = authConfigurations.find(isMediaShuttle);
    /**
     * If we don't have platform saml, then this portal can only be accessed
     * through media shuttle. An exclusively saml 2.0 portal is not accessible.
     * We do not allow access if the mediashuttle configuration has MFA enabled.
     */
    if (isNil(platformSamlConfiguration)) {
        return !isNil(mediaShuttleConfiguration) && !mediaShuttleConfiguration.mfaEnabled;
    }

    /**
     * We do have platform saml. If we only have platform saml or we have both platform saml and MS but
     * restricted domain tells us we must login to SAML then confirm loginIdentifiers.
     */
    var isPlatformDomainRestricted = !isNil(platformSamlConfiguration.restrictedDomains) && platformSamlConfiguration.restrictedDomains.includes(userEmailDomain);
    if (isNil(mediaShuttleConfiguration) || isPlatformDomainRestricted) {
        return !isNil(platformSamlConfiguration.loginIdentifier) && loginIdentifier === platformSamlConfiguration.loginIdentifier;
    }

    /**
     * Currently users can be logged in via one of two methods (username/password and SAML).
     * SAML logins always have a loginIdentifier while username/password authentication does not use this attribute.
     * If/when new methods of authentication are implemented this logic will need to be revisited.
     */
    var isUserLoggedInWithUsernamePassword = isNil(loginIdentifier);
    var isPortalConfiguredWithMfaForUsernamePassword = mediaShuttleConfiguration.mfaEnabled;
    /**
     * As MFA is not an option for platform username/password authentication, we do not allow access if the portal
     * has MFA enabled and the user has logged in with username/password.
     */
    if (isPortalConfiguredWithMfaForUsernamePassword && isUserLoggedInWithUsernamePassword) {
        return false;
    }

    /**
     * This has to be Platform SAML + MS. Platform is not domain restricted
     * so the portal is safely accessible through the MS config.
     */
    return true;
};

module.exports = {
    isPortalAccessible: isPortalAccessible
};